<template>
  <!-- Players Section -->
  <div class="w-full mx-auto py-4">
    <table class="w-full mb-4">
      <thead>
        <tr class="text-left">
          <th class="pb-2">Name</th>
          <th class="pb-2">No</th>
          <th class="pb-2">Size</th>
          <th class="pb-2">Qty</th>
          <th class="pb-2"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(player, index) in players"
          :key="`${'manual_roster_player_' + index}`"
          class="border-t"
        >
          <td class="py-2">
            <div>
              <input
                v-model="player.text"
                type="text"
                class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                :class="{
                  'border-red-500': errorMessages[`players[${index}].text`],
                }"
                :title="errorMessages[`players[${index}].text`]"
                @focusout="syncPlayersWithParent"
              />
            </div>
          </td>
          <td class="py-2">
            <input
              v-model="player.number"
              type="number"
              @focusout="syncPlayersWithParent"
              class="w-20 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </td>
          <td class="py-2">
            <div v-if="isCustomProduct">
              <input
                v-model="player.size"
                @focusout="syncPlayersWithParent"
                type="text"
                class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                :class="{
                  'border-red-500 focus:ring-0':
                    errorMessages[`players[${index}].size`],
                }"
                :title="errorMessages[`players[${index}].size`]"
              />
            </div>
            <div v-else>
              <div class="flex w-100">
                <ExclamationCircleIcon
                  v-if="errorMessages[`players[${index}].size`]"
                  class="text-red-500"
                  style="font-size: 2px; height: 25px; width: 25px"
                />
                <v-select
                  v-model="player.size"
                  :options="product_sizes"
                  style="width: 100%"
                  :clearable="false"
                  append-to-body
                  placement="auto"
                  :title="errorMessages[`players[${index}].size`]"
                />
              </div>
            </div>
          </td>
          <td class="py-2">
            <div>
              <input
                v-model="player.quantity"
                min="1"
                type="number"
                @focusout="syncPlayersWithParent"
                class="w-20 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                :class="{
                  'border-red-500 focus:ring-0':
                    errorMessages[`players[${index}].quantity`],
                }"
                :title="errorMessages[`players[${index}].quantity`]"
              />
            </div>
          </td>
          <td class="py-2">
            <button
              type="button"
              :key="`remove_player_${index}`"
              @click="() => removePlayer(index)"
              class="p-2 text-red-500 hover:bg-red-50 rounded-full"
            >
              <XMarkIcon class="w-4 h-4" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <button
      type="button"
      @click="addPlayer"
      class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors"
    >
      Add Player
    </button>
  </div>
</template>

<script setup lang="ts">
import { ExclamationCircleIcon, XMarkIcon } from "@heroicons/vue/24/solid";
import { defineEmits, defineProps, onMounted, ref, watch } from "vue";

// Props being used in the template
const props = defineProps<{
  players: Array<any>;
  product_sizes: string[] | Array<any>;
  isCustomProduct: boolean | false;
  index: number | 0;
}>();

const emit = defineEmits(["update:players"]);

let errorMessages = ref([]);

onMounted(() => {
  if (Array.isArray(props.players) && props.players.length === 0) {
    props.players.push({
      text: "",
      number: "",
      size: "",
      quantity: 1,
      information: "",
    });
  }
});

watch(
  () => props.players,
  (newPlayers) => {
    if (Array.isArray(newPlayers) && newPlayers.length === 0) {
      const defaultPlayer = {
        text: "",
        number: "",
        size: "",
        quantity: 1,
        information: "",
      };
      newPlayers.push(defaultPlayer);
    }
  },
  { immediate: true, deep: true },
);

function addPlayer() {
  const newPlayer = {
    text: "",
    number: "",
    size: "",
    quantity: 1,
    information: "",
  };
  // Add the new player to the players array
  props.players.push(newPlayer);
}

function removePlayer(index: number) {
  // Remove the player at the specified index
  props.players.splice(index, 1);

  // Emit the updated players list and the current index
  emit("update:players", props.players, props.index);
}

function syncPlayersWithParent() {
  // Emit the updated players list and the current index
  emit("update:players", props.players, props.index);
}
</script>
